import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Progress } from "./ui/progress";
import { ArrowRight, CheckCircle2, Circle } from "lucide-react";
import { statusTone } from "../lib/commandCenterStatusTone";

/**
 * OnboardingChecklist (onboarding-gate-004) — the member's "here is what's
 * left, click to finish it" view over the canonical Step-1 checklist
 * (`onboarding_checklist` from /api/command-center/me, onboarding-gate-001).
 *
 * Renders one row per checklist key FROM THE RESPONSE — the row count is
 * never hardcoded (the backend owns the item set). Feature-detects: an old
 * backend without the key renders nothing rather than crashing the
 * dashboard. All-complete collapses to a compact confirmation strip.
 */
const OnboardingChecklist = ({ items, agreementInFlight, agreementStatus }) => {
  if (!Array.isArray(items) || items.length === 0) return null;

  const done = items.filter((i) => i.done);
  const allDone = done.length === items.length;

  if (allDone) {
    // forge-20260612-006: once the member is in the partner stage, the
    // strip reports where the agreement IS instead of inviting them to
    // start one they already signed.
    if (agreementInFlight) {
      return (
        <Card className="mb-6 bg-blue-50 border-blue-200" data-testid="onboarding-checklist-agreement-pending">
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <CheckCircle2 className="text-blue-600 shrink-0" size={20} />
            <p className="text-sm text-blue-900 flex-1 min-w-48">
              <span className="font-semibold">Participation agreement</span> —{" "}
              {/* Member-facing label, never the raw status: the raw string
                  names the vendor ("FinBPM Agreement Sent") and this strip
                  is now on the landing page (STARSEED-CAPMARKETS-257). */}
              {agreementStatus ? statusTone(agreementStatus).label : "in progress with the partner"}.
            </p>
            <Link to="/partner-agreement" data-testid="view-partner-agreement">
              <span className="inline-flex items-center gap-1 rounded-md bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold px-4 py-2 transition-colors">
                View your agreement <ArrowRight size={14} />
              </span>
            </Link>
          </CardContent>
        </Card>
      );
    }
    return (
      <Card className="mb-6 bg-emerald-50 border-emerald-200" data-testid="onboarding-checklist-complete">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <CheckCircle2 className="text-emerald-600 shrink-0" size={20} />
          <p className="text-sm text-emerald-900 flex-1 min-w-48">
            <span className="font-semibold">Onboarding complete</span> — you can
            now proceed to the partner participation agreement.
          </p>
          <Link to="/partner-agreement" data-testid="proceed-partner-agreement">
            <span className="inline-flex items-center gap-1 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-4 py-2 transition-colors">
              Proceed to Partner Agreement <ArrowRight size={14} />
            </span>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6 bg-white border-slate-200" data-testid="onboarding-checklist">
      <CardHeader>
        <CardTitle className="text-slate-900">Complete your onboarding</CardTitle>
        <CardDescription className="text-slate-600">
          {done.length} of {items.length} steps complete — finish the items
          below so our team can review your file.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Progress value={Math.round((done.length / items.length) * 100)} className="mb-4" />
        <div className="divide-y divide-slate-100">
          {items.map((item) =>
            item.done ? (
              <div
                key={item.key}
                className="flex items-center gap-3 py-2.5"
                data-testid={`checklist-row-${item.key}`}
              >
                <CheckCircle2 size={18} className="text-emerald-600 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900">{item.label}</p>
                  <p className="text-xs text-slate-500">{item.detail}</p>
                </div>
              </div>
            ) : (
              <Link
                key={item.key}
                to={item.portal_path || "/command-center"}
                className="flex items-center gap-3 py-2.5 group"
                data-testid={`checklist-row-${item.key}`}
              >
                <Circle size={18} className="text-slate-300 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900 group-hover:text-blue-700">
                    {item.label}
                  </p>
                  <p className="text-xs text-slate-500">{item.detail}</p>
                </div>
                <ArrowRight
                  size={16}
                  className="text-slate-300 group-hover:text-blue-600 shrink-0"
                />
              </Link>
            )
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default OnboardingChecklist;
